import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import SegmentedToggle from '../../components/SegmentedToggle';
import { Cell } from '../../components/InlineCells';
import { useP } from '../../i18n/useP';
import { formatNumber } from '../../utils/format';
import './SeasonProfilesPanel.css';

/* LES PROFILS DE SAISONNALITÉ (décisions d'Olivier, 2026-10-11, proto validé
   dans la conversation) : la LISTE à gauche, en mini-cartes avec leur courbe,
   et le profil choisi à droite ; rien ne se décale quand on change de profil.
   La courbe se règle À LA SOURIS (le modèle de la Simulation liquidité) ou
   mois par mois sous elle, dans l'une de trois vues :
     Indices      l'écart au mois moyen, autour d'une ligne centrale à 0 ;
     % de l'année la part de chaque mois, l'année faisant 100 % : changer un
                  mois réduit ou augmente les autres en proportion ;
     % cumulé     le cumul depuis janvier, décembre fixé à 100 % : la courbe en
                  S d'un projet s'y lit, et un mois reste entre ses voisins.
   Les trois vues lisent le MÊME profil, enregistré en indices base 100. */

const MOIS = (p) => [p('Jan'), p('Fév'), p('Mar'), p('Avr'), p('Mai'), p('Juin'),
                     p('Juil'), p('Août'), p('Sep'), p('Oct'), p('Nov'), p('Déc')];
const somme = (a) => a.reduce((x, y) => x + y, 0);
const parts = (w) => { const s = somme(w) || 1; return w.map((v) => (v / s) * 100); };
const cumul = (w) => { let c = 0; return parts(w).map((v) => (c += v)); };
const ecarts = (w) => { const m = somme(w) / 12 || 1; return w.map((v) => (v / m - 1) * 100); };
// Les indices enregistrés : base 100, la moyenne de l'année valant 100.
const base100 = (w) => { const s = somme(w) || 1; return w.map((v) => Math.round(((v / s) * 1200) * 10) / 10); };

/* La valeur affichée d'un mois, et le profil qui en découle quand on la change. */
function lire(vue, w) {
  if (vue === 'i') return ecarts(w);
  return vue === 'p' ? parts(w) : cumul(w);
}
function poser(vue, w, i, v) {
  if (vue === 'i') {
    const m = somme(w) / 12;
    return w.map((x, k) => (k === i ? Math.max(0, m * (1 + v / 100)) : x));
  }
  if (vue === 'p') {
    const s = parts(w);
    const val = Math.max(0, Math.min(100, v));
    const reste = somme(s) - s[i];
    return s.map((x, k) => (k === i ? val : reste > 0 ? (x * (100 - val)) / reste : (100 - val) / 11));
  }
  if (i === 11) return w;                                   // décembre fait 100 %
  const c = cumul(w);
  c[i] = Math.max(i ? c[i - 1] : 0, Math.min(c[i + 1], v));
  return c.map((x, k) => x - (k ? c[k - 1] : 0));
}
const etiquette = (vue, v) => {
  if (vue === 'i') { const r = Math.round(v); return r > 0 ? `+${r}` : `${r}`; }
  return `${Math.round(v)} %`;
};

/* La courbe d'une mini-carte : courte, à droite du nom, colorée dessous
   (le modèle des lignes de la Simulation liquidité, 2026-10-11). */
function Apercu({ w }) {
  const L = 110, H = 44, mx = Math.max(...w, 1);
  const pts = w.map((v, i) => `${(i * L) / 11},${H - 2 - (v / mx) * (H - 8)}`).join(' ');
  return (
    <svg className="spp-apercu" viewBox={`0 0 ${L} ${H}`} preserveAspectRatio="none" aria-hidden="true">
      <polygon points={`0,${H} ${pts} ${L},${H}`} className="spp-apercu__aire" />
      <polyline points={pts} className="spp-trait" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

const VW = 640, VH = 230, PG = 48, PD = 12, PH = 16, PB = 24;

function Courbe({ vue, w, canWrite, onTirer, onLacher, mois }) {
  const svg = useRef(null);
  const [glisse, setGlisse] = useState(null);
  const vals = lire(vue, w);
  /* L'échelle ne bouge pas pendant qu'on tire, sans quoi le point fuirait sous
     la souris : elle se fige au premier geste. */
  const echelleLibre = useMemo(() => {
    if (vue === 'i') { const m = Math.max(20, ...vals.map(Math.abs)) * 1.2; return { bas: -m, haut: m }; }
    if (vue === 'c') return { bas: 0, haut: 110 };
    return { bas: 0, haut: Math.max(...vals) * 1.25 || 1 };
  }, [vue, vals]);
  const [echelleFigee, setEchelleFigee] = useState(null);
  const { bas, haut } = echelleFigee || echelleLibre;
  const x = (i) => PG + (i * (VW - PG - PD)) / 11;
  const y = (v) => PH + ((VH - PH - PB) * (haut - v)) / (haut - bas);
  const graduations = vue === 'i' ? [bas, bas / 2, 0, haut / 2, haut] : vue === 'c' ? [0, 25, 50, 75, 100] : [0, haut / 2, haut];
  const ligne = vals.map((v, i) => `${x(i)},${y(v)}`).join(' ');
  const zero = vue === 'i' ? y(0) : y(0);

  const valeurA = (e) => {
    const r = svg.current.getBoundingClientRect();
    const yy = ((e.clientY - r.top) * VH) / r.height;
    return haut - ((yy - PH) * (haut - bas)) / (VH - PH - PB);
  };
  const debut = (i) => (e) => {
    if (!canWrite || (vue === 'c' && i === 11)) return;
    e.preventDefault();
    svg.current.setPointerCapture(e.pointerId);
    setEchelleFigee({ bas, haut });
    setGlisse(i);
  };
  const bouge = (e) => { if (glisse !== null) onTirer(glisse, valeurA(e)); };
  const fin = () => {
    if (glisse === null) return;
    setGlisse(null);
    setEchelleFigee(null);
    onLacher();
  };

  return (
    <svg ref={svg} className="spp-courbe" viewBox={`0 0 ${VW} ${VH}`} role="img"
         aria-label={mois.map((m, i) => `${m} ${etiquette(vue, vals[i])}`).join(', ')}
         onPointerMove={bouge} onPointerUp={fin} onPointerCancel={fin}>
      {graduations.map((t) => {
        const fort = (vue === 'i' && Math.abs(t) < 1e-9) || (vue === 'c' && t === 100);
        return (
          <g key={t}>
            <line x1={PG} x2={VW - PD} y1={y(t)} y2={y(t)} className={fort ? 'spp-zero' : 'spp-grille'} />
            <text x={PG - 6} y={y(t) + 4} textAnchor="end" className={fort ? 'spp-axe spp-axe--fort' : 'spp-axe'}>
              {etiquette(vue, t)}
            </text>
          </g>
        );
      })}
      <polygon points={`${x(0)},${zero} ${ligne} ${x(11)},${zero}`} className="spp-aire" />
      <polyline points={ligne} className="spp-trait" />
      {vals.map((v, i) => {
        const fixe = vue === 'c' && i === 11;
        return (
          <g key={i}>
            <circle cx={x(i)} cy={y(v)} r={6} onPointerDown={debut(i)}
                    className={`spp-point${fixe ? ' is-fixe' : ''}${canWrite && !fixe ? ' is-mobile' : ''}`} />
            <text x={x(i)} y={vue === 'i' && v < 0 ? y(v) + 18 : y(v) - 10} textAnchor="middle" className="spp-val">
              {etiquette(vue, v)}
            </text>
            <text x={x(i)} y={VH - 6} textAnchor="middle" className="spp-axe">{mois[i]}</text>
          </g>
        );
      })}
    </svg>
  );
}

export default function SeasonProfilesPanel({ profils, canWrite, onUpdate, onDelete, onMove }) {
  const p = useP();
  const mois = MOIS(p);
  const [choisi, setChoisi] = useState(null);
  const [vue, setVue] = useState('i');
  const [brouillon, setBrouillon] = useState(null);   // { id, w } pendant qu'on tire
  const profil = profils.find((x) => x.id === choisi) || profils[0] || null;
  useEffect(() => { setBrouillon(null); }, [profils]);
  if (!profil) return <div className="om-empty">{p('Aucun profil.')}</div>;
  const w = brouillon && brouillon.id === profil.id ? brouillon.w : (profil.weights || []).map(Number);
  const vals = lire(vue, w);
  const usage = (n) => (n ? `${p('Utilisé par')} ${n} ${n > 1 ? p('lignes') : p('ligne')}` : p('Inutilisé'));
  const enregistrer = (poids) => onUpdate(profil.id, { weights: base100(poids) });

  return (
    <div className="spp">
      <div className="spp-liste">
        {profils.map((x, k) => (
          <div key={x.id} role="button" tabIndex={0}
               className={`spp-carte${x.id === profil.id ? ' is-on' : ''}${x.standard ? '' : ' spp-carte--perso'}`}
               onClick={() => setChoisi(x.id)}
               onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setChoisi(x.id); } }}>
            <span className="spp-carte__txt">
              <span className="spp-nom">{x.name}</span>
              <span className="spp-usage">{x.used_by ? `${x.used_by} ${x.used_by > 1 ? p('lignes') : p('ligne')}` : p('Inutilisé')}</span>
            </span>
            <Apercu w={(x.weights || []).map(Number)} />
            {/* RECLASSER (demande d'Olivier, 2026-10-11) : monter ou descendre d'un rang. */}
            {canWrite && onMove && (
              <span className="spp-ordre">
                <button type="button" aria-label={p('Monter')} disabled={k === 0}
                        onClick={(e) => { e.stopPropagation(); onMove(x.id, -1); }}><ChevronUp size={14} aria-hidden="true" /></button>
                <button type="button" aria-label={p('Descendre')} disabled={k === profils.length - 1}
                        onClick={(e) => { e.stopPropagation(); onMove(x.id, 1); }}><ChevronDown size={14} aria-hidden="true" /></button>
              </span>
            )}
          </div>
        ))}
      </div>
      <div className="spp-editeur">
        <div className="spp-tete">
          {/* Le nom, sa croix juste à côté, et l'usage dessous (2026-10-11). */}
          <span className="spp-ident">
            <span className="spp-ident__nom">
              <span className="spp-titre">
                <Cell value={profil.name} canWrite={canWrite} onSave={(v) => v && onUpdate(profil.id, { name: v })} />
              </span>
              {canWrite && !profil.used_by && (
                <button type="button" className="spp-suppr" aria-label={p('Supprimer')} title={p('Supprimer')}
                        onClick={() => {
                          if (window.confirm(p('Supprimer ce profil ?'))) onDelete(profil.id);
                        }}>✕</button>
              )}
            </span>
            <span className="spp-usage">{usage(profil.used_by)}</span>
          </span>
          {/* Un profil de base modifié reprend son nom et ses indices d'origine (2026-10-11). */}
          {canWrite && profil.modified && (
            <button type="button" className="lk-btn lk-btn--quiet" onClick={() => onUpdate(profil.id, { reset: true })}>
              ↺ {p('Profil d’origine')}
            </button>
          )}
          <span className="spp-vues">
            <SegmentedToggle value={vue} onChange={setVue}
                             options={[{ value: 'i', label: p('Indices') }, { value: 'p', label: p('% de l’année') },
                                       { value: 'c', label: p('% cumulé') }]} />
          </span>
        </div>
        <Courbe vue={vue} w={w} canWrite={canWrite} mois={mois}
                onTirer={(i, v) => setBrouillon({ id: profil.id, w: poser(vue, w, i, v) })}
                onLacher={() => brouillon && enregistrer(brouillon.w)} />
        <div className="spp-mois">
          {vals.map((v, i) => (
            <span key={i} className="spp-mois__c">
              <Cell value={vue === 'i' ? Math.round(v) : Math.round(v * 10) / 10} type="number"
                    canWrite={canWrite && !(vue === 'c' && i === 11)}
                    format={(z) => (z == null ? '' : vue === 'i' ? etiquette('i', z) : `${formatNumber(z, 1)}`)}
                    onSave={(z) => z != null && enregistrer(poser(vue, w, i, z))} />
              <span className="spp-mois__l">{mois[i]}</span>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
